import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, CssBaseline, Skeleton, Avatar, IconButton, useMediaQuery, useTheme } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useVendorAuthStore } from '@/store/vendorAuthStore';
import { useShop3OrderStore } from '@/store/shop3OrderStore';
import { useShopStore } from '@/store/shopStore';
import { useRoleBasedAuth } from '@/hooks/useRoleBasedAuth';
import { MAX_CONTENT_WIDTH } from '@/constants/layout';
import { getShopColors } from '@/constants/shopColors';
import RecentProductsSection from '@/components/RecentProductsSection';
import { getShop3MySubscription, type Shop3UserSubscription } from '@/services/shop3.service';
import Nav from '../Home/Nav';
import HomeContainer from '../Home/HomeContainer';
import MemberInfoCard from './components/MemberInfoCard';
import MenuListCard from './components/MenuListCard';

/**
 * 도매 사용자 주문관리
 * - 회원/자산 정보 통합 카드
 * - 메뉴 목록
 * - 최근 본 상품 섹션
 */
const SupplierMypage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { vendorPath } = useParams<{ vendorPath: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const recentProductsRef = useRef<HTMLDivElement>(null);

    // 도매 색상
    const colors = getShopColors('supplier');

    // 인증 정보
    const { isLoggedIn, openLoginDialog } = useRoleBasedAuth();
    const vendorId = useVendorAuthStore((state) => state.vendorId);
    const vendorInfo = useVendorAuthStore((state) => state.vendorInfo);

    // 현재 접속한 도매처 정보
    const { shop } = useShopStore();

    // 캐시/예치금 정보
    const wallet = useShop3OrderStore((state) => state.wallet);
    const deposits = useShop3OrderStore((state) => state.deposits);
    const isWalletLoading = useShop3OrderStore((state) => state.isWalletLoading);
    const fetchWalletAndDeposits = useShop3OrderStore((state) => state.fetchWalletAndDeposits);

    // 구독 정보
    const [subscription, setSubscription] = useState<Shop3UserSubscription | null>(null);
    const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);

    // 구독 정보 조회
    const fetchSubscription = useCallback(async (userId: string) => {
        setIsSubscriptionLoading(true);
        try {
            const subscription = await getShop3MySubscription(userId);
            setSubscription(subscription);
        } catch {
            setSubscription(null);
        } finally {
            setIsSubscriptionLoading(false);
        }
    }, []);

    // 로그인 체크
    useEffect(() => {
        if (!isLoggedIn) {
            openLoginDialog(`/${vendorPath}/mypage`);
        }
    }, [isLoggedIn, openLoginDialog, vendorPath]);

    // 캐시/예치금 및 구독 정보 로드
    useEffect(() => {
        if (isLoggedIn && vendorId) {
            fetchWalletAndDeposits(vendorId);
            fetchSubscription(vendorId);
        }
    }, [isLoggedIn, vendorId, fetchWalletAndDeposits, fetchSubscription]);

    // URL hash가 #recent-products인 경우 해당 섹션으로 스크롤
    useEffect(() => {
        if (isLoggedIn && location.hash === '#recent-products') {
            // 렌더링 후 스크롤을 위해 약간의 지연
            const timer = setTimeout(() => {
                recentProductsRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isLoggedIn, location.hash]);

    // 최근 본 상품 섹션으로 스크롤
    const scrollToRecentProducts = () => {
        recentProductsRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // 로그인 안 된 경우 로딩 표시
    if (!isLoggedIn) {
        // 모바일 레이아웃
        if (isMobile) {
            return (
                <>
                    <CssBaseline />
                    <Box sx={{ minHeight: '100vh', width: '100%', maxWidth: '100%', bgcolor: '#f5f5f5', overflowX: 'hidden' }}>
                        {/* 모바일 헤더 */}
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                bgcolor: '#fff',
                                zIndex: 10,
                                borderBottom: '1px solid #eee',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                                <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }} aria-label="뒤로가기">
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography sx={{ fontSize: 18, fontWeight: 700 }}>주문관리</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ px: 2, pt: 3 }}>
                            <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3, mb: 2 }} />
                            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
                        </Box>
                    </Box>
                </>
            );
        }

        // PC 레이아웃
        return (
            <HomeContainer>
                <CssBaseline />
                <Nav />
                <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pt: 3 }}>
                    <Box sx={{ maxWidth: MAX_CONTENT_WIDTH, mx: 'auto', px: { xs: 2, sm: 3 } }}>
                        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 3, mb: 2 }} />
                        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
                    </Box>
                </Box>
            </HomeContainer>
        );
    }

    // 공통 컨텐츠 (PC/모바일 공유)
    const mainContent = (
        <>
            {/* 사용자 정보 (카드 외부) */}
            <Box
                onClick={() => navigate(`/${vendorPath}/settings`)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                    cursor: 'pointer',
                }}
            >
                <Avatar
                    src={vendorInfo?.profileImage}
                    sx={{
                        width: 32,
                        height: 32,
                        bgcolor: '#e0e0e0',
                    }}
                >
                    {!vendorInfo?.profileImage && <PersonOutlineIcon sx={{ fontSize: 18, color: '#999' }} />}
                </Avatar>
                <Typography
                    sx={{
                        fontSize: { xs: 15, sm: 16 },
                        fontWeight: 500,
                        color: '#222',
                    }}
                >
                    {vendorInfo?.storeName || vendorInfo?.nickname || '사용자'}
                </Typography>
                <ChevronRightIcon sx={{ fontSize: 18, color: '#999' }} />
            </Box>

            {/* 플랜/자산 정보 카드 */}
            <MemberInfoCard
                wallet={wallet}
                isLoading={isWalletLoading}
                colors={colors}
                subscription={subscription}
                isSubscriptionLoading={isSubscriptionLoading}
                deposits={deposits}
                currentSupplierId={shop?.id}
                currentSupplierName={shop?.storeName ?? shop?.nickname}
                vendorPath={vendorPath}
            />

            {/* 메뉴 목록 카드 */}
            <MenuListCard vendorPath={vendorPath || ''} onRecentProductsClick={scrollToRecentProducts} />

            {/* 최근 본 상품 섹션 */}
            <Box ref={recentProductsRef}>
                <RecentProductsSection isMobileView={isMobile} />
            </Box>
        </>
    );

    // 모바일 레이아웃
    if (isMobile) {
        return (
            <>
                <CssBaseline />
                <Box sx={{ minHeight: '100vh', width: '100%', maxWidth: '100%', bgcolor: '#f5f5f5', overflowX: 'hidden' }}>
                    {/* 모바일 헤더 */}
                    <Box
                        sx={{
                            position: 'sticky',
                            top: 0,
                            bgcolor: '#fff',
                            zIndex: 10,
                            borderBottom: '1px solid #eee',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                            <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }} aria-label="뒤로가기">
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>주문관리</Typography>
                        </Box>
                    </Box>

                    {/* 메인 컨텐츠 */}
                    <Box sx={{ px: 2, pt: 3, pb: 10 }}>
                        {mainContent}
                    </Box>
                </Box>
            </>
        );
    }

    // PC 레이아웃
    return (
        <HomeContainer>
            <CssBaseline />
            <Nav />

            <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pb: 10 }}>
                <Box sx={{ maxWidth: MAX_CONTENT_WIDTH, mx: 'auto', px: 3, pt: 3 }}>
                    {/* 페이지 타이틀 */}
                    <Typography
                        sx={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#222',
                            mb: 4,
                            textAlign: 'center',
                        }}
                    >
                        주문관리
                    </Typography>

                    {mainContent}
                </Box>
            </Box>
        </HomeContainer>
    );
};

export default SupplierMypage;
